import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { QueryStatus, authAxios } from "../../../utils";
import { RootState } from "../../../redux/store";
import { fakeElectricCharges } from "./electricChargingFakeData";
import { ElectricCharge } from "./electricChargingAPI";

export interface ElectricChargingState {
  electricChargesStatus: QueryStatus;
  electricCharges: ElectricCharge[];
  reimburseChargeStatus: QueryStatus;
}

const initialState: ElectricChargingState = {
  electricChargesStatus: "idle",
  electricCharges: [],
  reimburseChargeStatus: "idle",
};

// Simulates the future backend endpoint returning the MERGED electric charges
// (CasaWatt home + our EMSP CDRs + card charges), unified format — until the API is wired.
// Only authorized charges are returned (authorizationStatus === "ALLOWED"), so the
// authorization status is not displayed in the table.
export const getElectricChargesAsync = createAsyncThunk(
  "getElectricChargesAsync/call",
  () => {
    return fakeElectricCharges;
  },
);

export const reimburseChargeAsync = createAsyncThunk(
  "reimburseCharge/call",
  async (chargeUuid: string, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    if (!company) throw new Error("Company not selected");
    const axios = authAxios({ companyUuid: company.uuid });
    await axios.post(`electric_charges/${chargeUuid}/reimburse`);
  },
);

export const electricChargingSlice = createSlice({
  name: "electricCharging",
  initialState,
  reducers: {
    resetElectricChargesStatus(state) {
      state.electricChargesStatus = "idle";
    },
    resetReimburseChargeStatus(state) {
      state.reimburseChargeStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getElectricChargesAsync.pending, (state) => {
        state.electricChargesStatus = "processing";
      })
      .addCase(getElectricChargesAsync.fulfilled, (state, action) => {
        state.electricChargesStatus = "success";
        state.electricCharges = action.payload;
      })
      .addCase(getElectricChargesAsync.rejected, (state) => {
        state.electricChargesStatus = "failed";
      })
      .addCase(reimburseChargeAsync.pending, (state) => {
        state.reimburseChargeStatus = "processing";
      })
      .addCase(reimburseChargeAsync.fulfilled, (state) => {
        state.reimburseChargeStatus = "success";
      })
      .addCase(reimburseChargeAsync.rejected, (state) => {
        state.reimburseChargeStatus = "failed";
      });
  },
});

export const selectElectricChargesStatus = (state: RootState) =>
  state.electricCharging.electricChargesStatus;

export const selectElectricCharges = (state: RootState) =>
  state.electricCharging.electricCharges;

export const selectReimburseChargeStatus = (state: RootState) =>
  state.electricCharging.reimburseChargeStatus;

export default electricChargingSlice.reducer;
